import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import useTrendResearchFilterParams from '@features/trend-research/hooks/useTrendResearchFilterParams';
import ImageSearchModal from '@features/trend-research/components/common/imageSearch/ImageSearchModal';
import ExploreDesign from '@features/trend-research/components/onboarding/components/ExploreDesign';
import Influencers from '@features/trend-research/components/onboarding/components/Influencers';
import KeyColorOrTheme from '@features/trend-research/components/onboarding/components/KeyColorOrTheme';
import KeyFabrics from '@features/trend-research/components/onboarding/components/KeyFabrics';
import KeyGraphicsOrPrint from '@features/trend-research/components/onboarding/components/KeyGraphicsOrPrint';
import MarketAndCategoriesFilter from '@features/trend-research/components/onboarding/components/MarketAndCategoriesFilter';
import OnboardedBrands from '@features/trend-research/components/onboarding/components/OnboardedBrands';
import ProductInformationCharts from '@features/trend-research/components/onboarding/components/ProductInformationCharts';
import TrendTopFilter from '@features/trend-research/components/onboarding/components/TrendTopFilter';

import { clearGlobalFilter, updateTextItemInGlobalFilter } from '@store/actions/FilterAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import {
	saveTrendResearchUserPreference,
	setBrandInsightSummary,
	setInstagramInfluencerList,
	setTrendResearchDateRange,
	setTrendResearchOnboardedBrands,
	toggleAllBrandSelectionForFilter,
	toggleAllInstagramInfluencerList
} from '@store/actions/trend-research/TrendResearchAction';
import {
	useExploreProductsSource,
	useSelectedBrands,
	useSelectedInluencers
} from '@store/reducers/trend-research/TrendResearchReducer';

import {
	findAllBrandInsight,
	findAllInstagramInfluencer,
	findTrendInsight,
	findTrendResearchUserPreference
} from '@services/trendResearch';

import { changeDateFormat, getDateRange } from '@utils/Util';

const TREND_RESEARCH_TABS = {
	BRAND: 'Brand',
	INSTAGRAM: 'Instagram',
	Explore: 'Explore'
};

const TrendOnboarding = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const selectedBrands = useSelectedBrands();
	const sourceType = useExploreProductsSource();
	const selectedInfluencers = useSelectedInluencers();
	const [isShowPreference, setIsShowPreference] = useState(false);
	const [isShowImageSearch, setIsShowImageSearch] = useState(false);
	const [isSummaryCountSet, setIsSummaryCountSet] = useState(false);
	const filterData = useSelector((store) => store.filterFieldStore);
	const [isPreferenceAndBrandSet, setIsPreferenceAndBrandSet] = useState(false);
	const [activeTab, setActiveTab] = useState(location?.state?.source || TREND_RESEARCH_TABS.BRAND);
	const { queryParams } = useTrendResearchFilterParams(
		activeTab === 'Instagram' ? ['brandCodeList', 'fabricTypeList'] : ['instagramUserNameList']
	);

	useEffect(() => {
		let { start, end } = getDateRange('oneYearBack');
		let fromDate = moment(start).format('YYYY-MM-DD');
		let toDate = moment(end).format('YYYY-MM-DD');
		let data = {
			name: 'dateRange',
			key: fromDate + ',' + toDate,
			value:
				changeDateFormat(fromDate, 'YYYY-MM-DD', 'DD MMM, YYYY') +
				' - ' +
				changeDateFormat(toDate, 'YYYY-MM-DD', 'DD MMM, YYYY'),
			identifier: 'dateRange'
		};
		dispatch(updateTextItemInGlobalFilter(data));

		return () => {
			dispatch(clearGlobalFilter());
		};
	}, []);

	useEffect(() => {
		dispatch(setTrendResearchDateRange(filterData?.dateRange?.[0]));
	}, [filterData]);

	const togglePreference = (isShow) => {
		setIsShowPreference(isShow);
	};

	const getUserPreference = async () => {
		try {
			const data = await findTrendResearchUserPreference();
			dispatch(saveTrendResearchUserPreference(data));
		} catch {
			if (activeTab === 'Brand') {
				const timout = setTimeout(() => {
					setIsShowPreference(true);
				}, 1000);

				return () => clearTimeout(timout);
			}
		}
	};

	const getAllBrands = async () => {
		try {
			dispatch(openLoader());
			const data = await findAllBrandInsight();
			dispatch(setTrendResearchOnboardedBrands(data));
			if (selectedBrands.length === 0) {
				dispatch(toggleAllBrandSelectionForFilter({ selectedCodes: data.map((item) => item.code) }));
			}
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const getInfluencers = async () => {
		try {
			dispatch(openLoader());
			const data = await findAllInstagramInfluencer();
			dispatch(setInstagramInfluencerList(data));
			if (selectedInfluencers.length === 0) {
				dispatch(toggleAllInstagramInfluencerList(data.map((item) => item.userName)));
			}
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const getTrendInsightCount = async (queryParams) => {
		try {
			const data = await findTrendInsight(`${activeTab.toLowerCase()}-insight/count`, queryParams);
			dispatch(setBrandInsightSummary(data));
		} catch {
			/* empty */
		}
	};

	useEffect(() => {
		if (isPreferenceAndBrandSet && queryParams !== null) {
			(async () => {
				await Promise.all([getTrendInsightCount(queryParams)]);
				setIsSummaryCountSet(true);
			})();
		}
	}, [isPreferenceAndBrandSet, queryParams, activeTab]);

	const getBrandsOrInfluencerInfo = async () => {
		if (activeTab === 'Brand') {
			await getAllBrands();
		}
		if (activeTab === 'Instagram') {
			await getInfluencers();
		}
	};

	useEffect(() => {
		if (activeTab !== 'Explore') {
			(async () => {
				await Promise.all([getBrandsOrInfluencerInfo(), getUserPreference()]);
				setIsPreferenceAndBrandSet(true);
				navigate('.', {
					state: { source: activeTab }
				});
			})();
		} else {
			(async () => {
				await Promise.all([getUserPreference()]);
			})();
		}
	}, [activeTab]);

	const toggleImageSearchModal = (isShow) => {
		setIsShowImageSearch(isShow);
	};

	const renderActiveTabs = () => {
		const renderKeyItems = (isShowKeyFabric = false) => {
			return (
				<>
					<MarketAndCategoriesFilter
						activeTab={activeTab}
						isShowPreference={isShowPreference}
						togglePreference={togglePreference}
						getUserPreference={getUserPreference}
					/>
					{isShowKeyFabric && (
						<KeyFabrics isSummaryCountSet={isSummaryCountSet} source={activeTab} />
					)}
					<KeyColorOrTheme
						isSummaryCountSet={isSummaryCountSet}
						title="Key Color"
						keyType={`${activeTab.toUpperCase()}/key-color`}
						type={`key-color`}
						summaryField="noOfColors"
						source={activeTab}
					/>
					<KeyColorOrTheme
						isSummaryCountSet={isSummaryCountSet}
						title="Key Theme"
						keyType={`${activeTab.toUpperCase()}/key-theme`}
						type={`key-theme`}
						summaryField="noOfThemes"
						source={activeTab}
					/>
					<KeyGraphicsOrPrint
						isSummaryCountSet={isSummaryCountSet}
						title="Key Graphics"
						keyType={`${activeTab.toUpperCase()}/key-graphics`}
						type={`key-graphics`}
						summaryField="noOfGraphics"
						source={activeTab}
					/>
					<KeyGraphicsOrPrint
						isSummaryCountSet={isSummaryCountSet}
						title="Key Prints"
						keyType={`${activeTab.toUpperCase()}/key-prints`}
						type={`key-prints`}
						summaryField="noOfPrints"
						source={activeTab}
					/>
				</>
			);
		};

		switch (activeTab) {
			case TREND_RESEARCH_TABS.BRAND:
				return (
					<>
						<OnboardedBrands />
						<ProductInformationCharts isSummaryCountSet={isSummaryCountSet} />
						{renderKeyItems(true)}
					</>
				);
			case TREND_RESEARCH_TABS.INSTAGRAM:
				return (
					<>
						<Influencers />
						{renderKeyItems(false)}
					</>
				);
			case TREND_RESEARCH_TABS.Explore:
				return (
					<div className="explore-section border-0">
						<TrendTopFilter
							type={TREND_RESEARCH_TABS.Explore}
							toggleImageSearchModal={toggleImageSearchModal}
						/>
						<ExploreDesign />
					</div>
				);
			default:
				return <></>;
		}
	};

	return (
		<>
			<div className="top-header ">
				<div className="trend-list">
					<div className="page-title-area pb-2 d-flex align-items-center">
						<h1 className="semibold-16">TRENDS</h1>
					</div>
					<div className="navigation-tab bg-white">
						<ul>
							{Object.keys(TREND_RESEARCH_TABS)?.map((item) => (
								<li
									key={`trend_tab_${item}`}
									className={activeTab === TREND_RESEARCH_TABS[item] ? 'active' : ''}
									onClick={() => {
										window.scrollTo(0, 0);
										setIsSummaryCountSet(false);
										setIsPreferenceAndBrandSet(false);
										setActiveTab(TREND_RESEARCH_TABS[item]);
										navigate('.', {
											state: { source: TREND_RESEARCH_TABS[item] }
										});
									}}
								>
									{TREND_RESEARCH_TABS[item]}
								</li>
							))}
						</ul>
					</div>
				</div>
			</div>
			<section className="body-content trend-data-container bg-white">{renderActiveTabs()}</section>
			{isShowImageSearch && (
				<ImageSearchModal onClose={() => toggleImageSearchModal(false)} sourceType={sourceType} />
			)}
		</>
	);
};

export default TrendOnboarding;
